import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, Link2, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { uploadChunks, fileSize } from "@/utils/chunkedUpload";
import { MaterialRow } from "@/components/t4r/course/MaterialRow";

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.pps,.ppsx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.odp,.ods,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.m4v,.webm,.mp3,.wav,.m4a,.zip";
const MAX = 1024 * 1024 * 1024;

const LessonSelect = ({ lessons, value, onChange, testId, className = "" }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`h-8 rounded-md border border-border bg-white px-2 text-xs ${className}`} data-testid={testId}>
    <option value="">Whole course</option>
    {lessons.map((l, i) => <option key={l.id} value={l.id}>Lesson {i + 1}: {l.title}</option>)}
  </select>
);

const QueueItem = ({ q }) => (
  <div className="flex items-center gap-2 text-xs" data-testid="material-queue-item">
    {q.status === "done" ? <CheckCircle2 size={13} className="text-green-600" /> : q.status === "error" ? <XCircle size={13} className="text-rose-600" /> : <Loader2 size={13} className="animate-spin text-slate-400" />}
    <span className="w-56 truncate">{q.name}</span>
    <span className="w-16 text-muted-foreground">{fileSize(q.size)}</span>
    {q.status === "error" ? <span className="text-rose-600 truncate">{q.error}</span> : <Progress value={q.pct} className="h-1.5 flex-1" />}
  </div>
);

export const CourseMaterialsTab = ({ course, reload }) => {
  const [items, setItems] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [target, setTarget] = useState("");
  const [queue, setQueue] = useState([]);
  const [drag, setDrag] = useState(false);
  const [link, setLink] = useState({ title: "", url: "" });
  const inputRef = useRef(null);

  const load = useCallback(() => api.get(`/lms/admin/courses/${course.id}/materials`).then(({ data }) => setItems(data.materials || [])), [course.id]);
  useEffect(() => { load(); api.get(`/lms/admin/courses/${course.id}/lessons`).then(({ data }) => setLessons(data.lessons || [])); }, [course.id, load]);

  const patchQ = (id, p) => setQueue((qs) => qs.map((q) => (q.id === id ? { ...q, ...p } : q)));
  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const entries = list.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, size: f.size, pct: 0, status: "queued", file: f }));
    setQueue((qs) => [...qs.filter((q) => q.status !== "done"), ...entries]);
    let ok = 0;
    for (const e of entries) {
      if (e.size > MAX) { patchQ(e.id, { status: "error", error: "Over 1 GB" }); continue; }
      try {
        patchQ(e.id, { status: "uploading" });
        const { data: init } = await api.post(`/lms/admin/courses/${course.id}/materials/init`, { filename: e.name, size: e.size, content_type: e.file.type, lesson_id: target || null });
        await uploadChunks(init.upload_id, e.file, init.chunk_size, (pct) => patchQ(e.id, { pct }));
        await api.post(`/lms/admin/material-uploads/${init.upload_id}/complete`);
        patchQ(e.id, { status: "done", pct: 100 }); ok += 1;
      } catch (err) { patchQ(e.id, { status: "error", error: formatApiError(err.response?.data?.detail) || "Upload failed" }); }
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    load(); reload();
  };
  const addLink = async () => {
    try { await api.post(`/lms/admin/courses/${course.id}/materials/link`, { ...link, lesson_id: target || null }); setLink({ title: "", url: "" }); toast.success("Link added"); load(); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add link"); }
  };
  const patch = async (m, body) => {
    try { const { data } = await api.patch(`/lms/admin/resources/${m.id}`, body); setItems((xs) => xs.map((x) => (x.id === m.id ? data : x))); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"? Learners will lose access straight away.`)) return;
    try { await api.delete(`/lms/admin/resources/${m.id}`); setItems((xs) => xs.filter((x) => x.id !== m.id)); reload(); } catch { toast.error("Couldn't delete"); }
  };

  const groups = [{ id: "", title: "Whole course" }, ...lessons.map((l, i) => ({ id: l.id, title: `Lesson ${i + 1}: ${l.title}` }))];
  const busy = queue.some((q) => q.status === "uploading" || q.status === "queued");

  return (
    <div className="space-y-5" data-testid="course-materials-tab">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed p-6 transition-colors ${drag ? "border-green-500 bg-green-50" : "border-slate-200 bg-white"}`} data-testid="materials-dropzone">
        <div className="flex flex-wrap items-center gap-4">
          <UploadCloud size={32} className="text-green-700" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900">Drop files here or choose several at once</p>
            <p className="text-xs text-muted-foreground">PDF, Word, PowerPoint, Excel, images, video and audio — up to 1 GB each. Large files upload in chunks and resume if the connection drops.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Add to</span>
            <LessonSelect lessons={lessons} value={target} onChange={setTarget} testId="materials-target" />
            <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="materials-choose-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Choose files
            </Button>
          </div>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="materials-file-input" />
        </div>
        {queue.length > 0 && <div className="mt-4 space-y-1.5 border-t border-border pt-3" data-testid="materials-queue">{queue.map((q) => <QueueItem key={q.id} q={q} />)}</div>}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Link2 size={14} className="text-slate-400" />
          <Input value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} placeholder="Link title (optional)" className="h-8 w-56 text-xs" data-testid="materials-link-title" />
          <Input value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://…" className="h-8 flex-1 min-w-[200px] text-xs" data-testid="materials-link-url" />
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!link.url} onClick={addLink} data-testid="materials-link-add">Add link</Button>
        </div>
      </div>
      {items === null ? <Loader2 className="mx-auto animate-spin text-slate-400" /> : groups.map((g) => {
        const rows = items.filter((m) => (m.lesson_id || "") === g.id);
        if (!rows.length && g.id) return null;
        return (
          <section key={g.id || "course"} className="space-y-1.5" data-testid={`materials-group-${g.id || "course"}`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{g.title} <span className="font-normal normal-case">· {rows.length}</span></h3>
            <div className="divide-y divide-slate-100 rounded-xl border border-border bg-white">
              {rows.map((m) => <MaterialRow key={m.id} m={m} lessons={lessons} onPatch={(b) => patch(m, b)} onDelete={() => remove(m)} LessonSelect={LessonSelect} />)}
              {!rows.length && <p className="p-4 text-xs italic text-muted-foreground">No course-wide materials yet.</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
};
